import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  JustifiTransactionsSummary,
  Processor,
  StripeTransactionsSummary,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Table } from '@frontend/design-system';
import { useGetPayoutTransactionsV1Query } from '../../../hooks/use-get-payout';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { DetailCard } from '../../Card/detail-card';
import { Value } from '../PayoutValue/value';
import { generateStripeColumns, generateJustifiColumns } from './generate-columns';

export const StripePayoutSummaryTable = ({ payoutId, locationId }: { payoutId: string; locationId: string }) => {
  const { t } = useTranslation('payments');
  const columns = useMemo(() => generateStripeColumns(t), []);
  const { data, isLoading } = useGetPayoutTransactionsV1Query({
    payoutId,
    locationId,
    processor: Processor.STRIPE,
  });

  const stripeTransactionsSummary: StripeTransactionsSummary | undefined =
    data && 'stripeTransactionsSummary' in data && data?.stripeTransactionsSummary
      ? data?.stripeTransactionsSummary
      : undefined;

  return (
    <DetailCard>
      <Heading level={2}>{t('Summary')}</Heading>
      <Divider />
      <Table
        colConfig={columns}
        data={stripeTransactionsSummary?.payoutSummary?.payoutSummaryItems || []}
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        manualSortBy
        css={css`
          height: max-content;
        `}
        isLoading={isLoading}
        hasResponsiveColWidths
      />
      <div
        css={css`
          display: flex;
          flex-flow: row;
          justify-content: flex-end;
          margin: ${theme.spacing(3, 0)};
          > * {
            margin-left: ${theme.spacing(1)};
          }
        `}
      >
        <Value>{t('Payout Total')}</Value>
        {stripeTransactionsSummary && (
          <Value>
            {stripeTransactionsSummary.payoutSummary?.totalGross &&
              formatCentsToCurrency(stripeTransactionsSummary.payoutSummary.totalGross)}
          </Value>
        )}
      </div>
    </DetailCard>
  );
};

export const JustifiPayoutSummaryTable = ({ payoutId, locationId }: { payoutId: string; locationId: string }) => {
  const { t } = useTranslation('payments');
  const columns = useMemo(() => generateJustifiColumns(t), []);
  const { data, isLoading } = useGetPayoutTransactionsV1Query({
    payoutId,
    locationId,
    processor: Processor.JUSTIFI,
  });

  const justifiTransactionsSummary: JustifiTransactionsSummary | undefined =
    data && 'justifiTransactionsSummary' in data && data?.justifiTransactionsSummary
      ? data?.justifiTransactionsSummary
      : undefined;

  return (
    <DetailCard>
      <Heading level={2}>{t('Summary')}</Heading>
      <Divider />
      <Table
        colConfig={columns}
        data={justifiTransactionsSummary?.summary?.payoutSummaryItems || []}
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        manualSortBy
        css={css`
          height: max-content;
        `}
        isLoading={isLoading}
        hasResponsiveColWidths
      />
      <div
        css={css`
          display: flex;
          flex-flow: row;
          justify-content: flex-end;
          margin: ${theme.spacing(3, 0)};
          > * {
            margin-left: ${theme.spacing(1)};
          }
        `}
      >
        <Value>{t('Payout Total')}</Value>
        {justifiTransactionsSummary && (
          <Value>
            {justifiTransactionsSummary.summary?.totalGross &&
              formatCentsToCurrency(justifiTransactionsSummary.summary.totalGross)}
          </Value>
        )}
      </div>
    </DetailCard>
  );
};
